import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedMemo } from '@hooks/debounceAsync';
import { BrowserUtility } from '@utility/browser-utility';
import { CommonConstant } from '@utility/constants/common';

export type Sort = Record<string, 'asc' | 'desc'>;

export type PagedFilter = {
  limit: number;
  page: number;
  search: string;
  sort?: Sort;
  [key: string]: unknown;
};

export const pagedFilter = (sort?: Sort): PagedFilter => ({
  limit: CommonConstant.defaultPageSize,
  page: 1,
  search: '',
  ...(sort ? { sort } : {}),
});

/* The API takes the sort column as its own param: `{ sort: { name: 1 } }` goes out as `{ name: 1 }`. */
export const toListParams = ({ sort, ...rest }: PagedFilter) => ({
  ...rest,
  ...sort,
  sort: null,
});

/**
 * A table's page, size, search and sort. With `storageKey` it survives a
 * reload (the legacy table filter store). `applied` follows `params(filter)`
 * once it settles, so typing in a search asks once, not on every key.
 */
export function usePagedFilter<P>(
  initial: PagedFilter,
  params: (filter: PagedFilter) => P,
  storageKey?: string,
) {
  const [filter, setFilter] = useState<PagedFilter>(() =>
    storageKey ? BrowserUtility.getTableFilter(storageKey, initial) : initial,
  );

  useEffect(() => {
    if (storageKey) BrowserUtility.updateTableFilter(storageKey, filter);
  }, [storageKey, filter]);

  const next = params(filter);
  const applied = useDebouncedMemo<P>(() => next, [JSON.stringify(next)], 500);

  /*
   * Stable across renders: pages put these in memoized header content, and a
   * new one each render loops through the header context, which starves the
   * router's transitions (the page never leaves).
   */
  const filterChanged = useCallback(
    (key: string, value: unknown) =>
      setFilter(previous => ({ ...previous, page: 1, [key]: value })),
    [],
  );
  const pageChanged = useCallback(
    (page: number) => setFilter(previous => ({ ...previous, page })),
    [],
  );
  const updateAllFilter = useCallback(
    (values: Partial<PagedFilter>) =>
      setFilter(previous => ({ ...previous, page: 1, ...values })),
    [],
  );

  return useMemo(
    () => ({ filter, applied, filterChanged, pageChanged, updateAllFilter }),
    [filter, applied, filterChanged, pageChanged, updateAllFilter],
  );
}
